import type { BrandMetricsRates } from "@/services/api/brandMetricsRates";
import type { AIModel } from "@/services/api/models";
import queryQuestionsApi, {
  type QueryQuestionItem,
} from "@/services/api/queryQuestions";
import { DownOutlined, ReloadOutlined, UpOutlined } from "@ant-design/icons";
import { DatePicker, Select } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import "./BaseAnalysisSection.less";

interface Props {
  // 基础控制属性
  isMobile?: boolean;
  showPlatformControls?: boolean;
  models?: AIModel[];
  modelsLoading?: boolean;
  onPlatformChange?: (platform: string) => void;

  // 趋势数据属性
  loading?: boolean;
  error?: string | null;
  onRetry?: () => Promise<void>;

  // 品牌信息属性
  brandName?: string;
  industry?: string;
  onIndustryChange?: (industry: string) => void;
  industryOptions?: Array<{ value: string; label: string }>;

  // 品牌指标属性
  brandMetrics?: BrandMetricsRates | null;
  brandMetricsLoading?: boolean;

  // 新增：数据刷新回调
  onTimeRangeChange?: (
    timeRange:
      | "today"
      | "last7d"
      | "last30d"
      | number
      | { startDate: string; endDate: string }
  ) => void;
  onPlatformSelectionChange?: (platform: string) => void;

  // 初始日期范围（用于从 URL 复原）
  initialStartDate?: string;
  initialEndDate?: string;
  // 初始 qId（用于从 URL 复原）
  initialQId?: string;
  // 品牌和分类 ID（用于查询问题列表）
  brandId?: string;
  categoryId?: string;
  // qId 变化回调
  onQIdChange?: (qId: string) => void;
  // 指标类型（用于查询问题列表）
  metricType?: string;

  // 品牌+行业选择属性
  showBrandIndustryControls?: boolean;
  brandOptions?: Array<{ value: string; label: string }>;
  selectedBrand?: string;
  selectedIndustry?: string;
  onBrandChange?: (brand: string) => void;
  onIndustrySelectionChange?: (industry: string) => void;

  // 收起/展开状态变化回调
  onCollapseChange?: (isCollapsed: boolean) => void;
  // 外部控制的收起状态
  externalCollapsed?: boolean;
}

const BaseAnalysisSection: React.FC<Props> = ({
  isMobile = false,
  showPlatformControls = true,
  models = [],
  modelsLoading = false,
  onPlatformChange,
  // 暂时不使用的参数，用下划线前缀避免 TypeScript 警告
  loading: _loading = false,
  error: _error = null,
  onRetry: _onRetry,
  brandName: _brandName = "",
  industry: _industry = "",
  onIndustryChange: _onIndustryChange,
  industryOptions: _industryOptions = [],
  brandMetrics: _brandMetrics = null,
  brandMetricsLoading: _brandMetricsLoading = false,
  // 新增回调
  onTimeRangeChange,
  onPlatformSelectionChange,
  // 品牌+行业选择参数
  showBrandIndustryControls = false,
  selectedIndustry = "",
  onIndustrySelectionChange,
  // 初始日期范围
  initialStartDate,
  initialEndDate,
  // 初始 qId
  initialQId,
  // 品牌和分类 ID
  brandId,
  categoryId,
  // qId 变化回调
  onQIdChange,
  // 指标类型
  metricType = "mentionRate,firstMentionRate,firstRecommendationRate",
  // 收起/展开状态变化回调
  onCollapseChange,
  // 外部控制的收起状态
  externalCollapsed,
}) => {
  const { t } = useTranslation();

  // ==================== 状态定义 ====================
  // 时间相关状态
  const [timeKey, setTimeKey] = React.useState<
    "today" | "last7d" | "last30d" | "custom"
  >(initialStartDate && initialEndDate ? "custom" : "last7d");
  const [previousTimeKey, setPreviousTimeKey] = React.useState<
    "today" | "last7d" | "last30d" | "custom"
  >(initialStartDate && initialEndDate ? "custom" : "last7d");
  const [customStartDate, setCustomStartDate] = React.useState<string | null>(
    initialStartDate || null
  );
  const [customEndDate, setCustomEndDate] = React.useState<string | null>(
    initialEndDate || null
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);

  // 筛选相关状态
  const [platform, setPlatform] = React.useState<string>("all");
  const [selectedCountry, setSelectedCountry] = React.useState<string>("all");
  const [qId, setQId] = React.useState<string>(initialQId || "");
  const [questionList, setQuestionList] = React.useState<QueryQuestionItem[]>(
    []
  );
  const [questionLoading, setQuestionLoading] = React.useState(false);

  // UI 状态
  const [isCollapsed, setIsCollapsed] = React.useState(
    externalCollapsed ?? false
  );

  // Refs
  const rangePickerRef = React.useRef<any>(null);
  const hasSelectedDateRef = React.useRef<boolean>(false);

  // ==================== 副作用 ====================
  // 同步外部收起状态
  React.useEffect(() => {
    if (externalCollapsed !== undefined) {
      setIsCollapsed(externalCollapsed);
    }
  }, [externalCollapsed]);

  // 获取问题列表
  React.useEffect(() => {
    const fetchQuestions = async () => {
      if (!brandId || !categoryId) return;

      setQuestionLoading(true);
      try {
        const result = await queryQuestionsApi.getQueryQuestions({
          brandId,
          categoryId,
          days: 7,
          metricType,
          modelId: "all",
        });
        setQuestionList(result.list || []);
        // 设置默认问题
        // if (result.list && result.list.length > 0) {
        //   setQId(result.list[0].qId);
        // }
      } catch (error) {
        console.error("获取问题列表失败:", error);
        setQuestionList([]);
      } finally {
        setQuestionLoading(false);
      }
    };
    fetchQuestions();
  }, [brandId, categoryId, metricType]);

  // ==================== 常量定义 ====================
  // 禁用未来日期的判断函数
  const disabledFutureDate = (current: Dayjs) =>
    current && current > dayjs().endOf("day");

  // 时间选项
  const BASE_TIME_OPTIONS = [
    { label: t("search_analysis.time_yesterday"), value: "today" as const },
    { label: t("search_analysis.time_7"), value: "last7d" as const },
    { label: t("search_analysis.time_30"), value: "last30d" as const },
  ];
  const TIME_OPTIONS_EXPAND = BASE_TIME_OPTIONS;
  const TIME_OPTIONS_COLLAPSE = [
    ...BASE_TIME_OPTIONS,
    { label: t("search_analysis.time_custom"), value: "custom" as const },
  ];

  // 国家选项
  const COUNTRY_OPTIONS = [
    { label: t("search_analysis.all_countries"), value: "all" },
    { label: t("search_analysis.country_usa"), value: "usa" },
    { label: t("search_analysis.country_poland"), value: "poland" },
    { label: t("search_analysis.country_singapore"), value: "singapore" },
  ];

  // ==================== 辅助函数 ====================
  // 渲染平台选项（用于 Select 的 labelRender 和 optionRender）
  const renderPlatformItem = (
    value: string | number,
    fallbackLabel?: React.ReactNode
  ) => {
    if (value === "all") {
      return (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span>🌐</span> {t("search_analysis.all_platforms")}
        </span>
      );
    }
    const model = models.find((m) => m.id === value);
    if (model) {
      return (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {model.icon && (
            <img
              src={model.icon}
              alt={model.name}
              style={{ width: 14, height: 14, borderRadius: 2 }}
            />
          )}
          {model.name}
        </span>
      );
    }
    return fallbackLabel ?? null;
  };

  // ==================== 事件处理函数 ====================

  const handleTimeRangeChange = (
    newTimeRange: "today" | "last7d" | "last30d" | number
  ) => {
    if (typeof newTimeRange === "number") {
      setTimeKey("custom");
      onTimeRangeChange?.(newTimeRange);
    } else {
      setTimeKey(newTimeRange);
      onTimeRangeChange?.(newTimeRange);
    }
  };

  const handlePlatformChange = (newPlatform: string) => {
    setPlatform(newPlatform);
    onPlatformChange?.(newPlatform);
    onPlatformSelectionChange?.(newPlatform);
  };

  const handleReset = () => {
    // 1. Reset Time to default (last7d)
    setTimeKey("last7d");
    setPreviousTimeKey("last7d");
    setCustomStartDate(null);
    setCustomEndDate(null);
    onTimeRangeChange?.("last7d");

    // 2. Reset Platform to default (all)
    setPlatform("all");
    onPlatformChange?.("all");
    onPlatformSelectionChange?.("all");

    // 3. Reset Country to default (all)
    setSelectedCountry("all");

    // 4. Reset Industry to first option if available
    if (_industryOptions && _industryOptions.length > 0) {
      onIndustrySelectionChange?.(_industryOptions[0].value);
    }
  };

  // 处理日期范围选择
  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dates && dates[0] && dates[1]) {
      hasSelectedDateRef.current = true; // 标记已成功选择日期
      setCustomStartDate(dateStrings[0]);
      setCustomEndDate(dateStrings[1]);
      setTimeKey("custom");
      onTimeRangeChange?.({
        startDate: dateStrings[0],
        endDate: dateStrings[1],
      });
    }
  };

  // 处理 qId 变化
  const handleQIdChange = (value: string) => {
    setQId(value);
    onQIdChange?.(value);
  };

  return (
    <div
      className={`base-analysis-section-container ${isMobile ? "mobile" : ""}`}
    >
      <div className="panel">
        <div className="controls-wrapper">
          {/* Row 1: Brand & Industry + topic + query */}
          {showBrandIndustryControls && (
            <div className="filter-row">
              {/* 展开时显示标签 */}
              {!isCollapsed && (
                <span className="row-label">
                  {t("search_analysis.label_brand")}
                </span>
              )}
              <div className="brand-industry-wrapper">
                <span className="brand-name">{_brandName}</span>
                <Select
                  value={selectedIndustry}
                  onChange={onIndustrySelectionChange}
                  placeholder={t("search_detail.base_placeholder_industry")}
                  options={_industryOptions}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="industry-selector"
                  popupMatchSelectWidth={false}
                />

                {/* Placeholders for topic and query */}
                {/* <Select
                  placeholder={t("search_analysis.placeholder_topic")}
                  className="extra-selector"
                  disabled
                /> */}
                {/* 问题列表 */}
                {questionList.length > 0 && (
                  <Select
                    value={qId || ""}
                    onChange={handleQIdChange}
                    // placeholder={t("search_analysis.placeholder_query")}
                    className="extra-selector"
                    loading={questionLoading}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={[
                      {
                        label: t("search_analysis.all_queries"),
                        value: "",
                      },
                      ...questionList.map((item) => ({
                        label: item.question || item.qId, // 如果 question 为空，显示 qId
                        value: item.qId,
                      })),
                    ]}
                  />
                )}

                {isCollapsed && (
                  <>
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <Select
                        value={timeKey}
                        onSelect={(value) => {
                          if (value === "custom") {
                            // 选择自定义时，保存当前 timeKey 并打开日期范围选择器
                            if (timeKey !== "custom") {
                              setPreviousTimeKey(timeKey);
                            }
                            setTimeKey("custom");
                            setIsDatePickerOpen(true);
                          } else {
                            handleTimeRangeChange(
                              value as "today" | "last7d" | "last30d"
                            );
                            setPreviousTimeKey(
                              value as "today" | "last7d" | "last30d"
                            );
                            setCustomStartDate(null);
                            setCustomEndDate(null);
                          }
                        }}
                        options={TIME_OPTIONS_COLLAPSE.map((opt) => ({
                          ...opt,
                          // 如果是自定义且已选择日期，显示日期范围
                          label:
                            opt.value === "custom" &&
                            customStartDate &&
                            customEndDate
                              ? `${customStartDate} ~ ${customEndDate}`
                              : opt.label,
                        }))}
                        className="time-range-selector"
                        popupMatchSelectWidth={false}
                        style={{
                          minWidth:
                            timeKey === "custom" && customStartDate ? 200 : 120,
                        }}
                      />

                      {/* 日期范围选择器，用于自定义日期选择 - 绝对定位在 Select 下方 */}
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          width: "100%",
                          height: 0,
                          overflow: "visible",
                          zIndex: 1000,
                        }}
                      >
                        <DatePicker.RangePicker
                          ref={rangePickerRef}
                          open={isDatePickerOpen}
                          onOpenChange={(open) => {
                            setIsDatePickerOpen(open);
                            // 关闭弹窗时，如果没有成功选择日期，恢复之前的选项
                            if (!open) {
                              if (
                                !hasSelectedDateRef.current &&
                                (!customStartDate || !customEndDate)
                              ) {
                                setTimeKey(previousTimeKey);
                              }
                              // 重置标记
                              hasSelectedDateRef.current = false;
                            }
                          }}
                          value={
                            customStartDate && customEndDate
                              ? [dayjs(customStartDate), dayjs(customEndDate)]
                              : null
                          }
                          onChange={(dates, dateStrings) =>
                            handleRangeChange(dates, dateStrings)
                          }
                          style={{
                            width: "1px",
                            height: "1px",
                            padding: 0,
                            border: "none",
                            opacity: 0,
                            position: "absolute",
                            top: 0,
                            left: 0,
                            pointerEvents: "none",
                          }}
                          disabledDate={disabledFutureDate}
                          getPopupContainer={(trigger) =>
                            trigger.parentNode as HTMLElement
                          }
                        />
                      </div>
                    </div>

                    {/* 收起时：平台下拉选择 */}
                    {showPlatformControls && (
                      <Select
                        value={platform}
                        onChange={handlePlatformChange}
                        className="platform-selector"
                        popupMatchSelectWidth={false}
                        style={{ minWidth: 140 }}
                        labelRender={({ value }) =>
                          renderPlatformItem(String(value))
                        }
                        optionRender={(option) =>
                          renderPlatformItem(
                            String(option.value ?? ""),
                            option.label
                          )
                        }
                        options={[
                          {
                            label: t("search_analysis.all_platforms"),
                            value: "all",
                          },
                          ...models.map((model) => ({
                            label: model.name,
                            value: model.id,
                          })),
                        ]}
                      />
                    )}

                    {/* Country Selector (Collapsed) */}
                    {/* <Select
                      value={selectedCountry}
                      onChange={setSelectedCountry}
                      options={COUNTRY_OPTIONS}
                      className="country-selector"
                      popupMatchSelectWidth={false}
                      style={{ minWidth: 120 }}
                    /> */}
                  </>
                )}
              </div>
            </div>
          )}

          {/* 展开时的原始布局 (Collapsible Section) */}
          {!isCollapsed && (
            <>
              {/* Row 2: Time Range */}
              <div className="filter-row time-range-row">
                <span className="row-label">
                  {t("search_analysis.label_time_range")}
                </span>
                <div className="time-range-buttons">
                  {TIME_OPTIONS_EXPAND.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`time-button ${timeKey === opt.value && !customStartDate ? "active" : ""}`}
                      onClick={() => {
                        handleTimeRangeChange(opt.value);
                        setCustomStartDate(null);
                        setCustomEndDate(null);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}

                  {/* Custom Date Range Picker */}
                  <DatePicker.RangePicker
                    value={
                      customStartDate && customEndDate
                        ? [dayjs(customStartDate), dayjs(customEndDate)]
                        : null
                    }
                    onChange={handleRangeChange}
                    className="custom-date-picker"
                    classNames={
                      isMobile
                        ? { popup: { root: "mobile-date-picker-dropdown" } }
                        : undefined
                    }
                    placeholder={[
                      t("search_analysis.action_select_start_date"),
                      t("search_analysis.action_select_end_date"),
                    ]}
                    format="YYYY-MM-DD"
                    disabledDate={disabledFutureDate}
                    allowClear={false}
                    inputReadOnly={isMobile}
                  />
                </div>
              </div>

              {/* Row 3: Platform */}
              {showPlatformControls && (
                <div className="filter-row ">
                  <span className="row-label">
                    {t("search_analysis.label_platform")}
                  </span>
                  <div className="platform-buttons-container">
                    <button
                      type="button"
                      className={`platform-button all-button ${platform === "all" ? "active" : ""}`}
                      onClick={() => handlePlatformChange("all")}
                    >
                      <span className="icon-globe">🌐</span>{" "}
                      {t("search_analysis.all_platforms")}
                    </button>
                    {models.map((model) => (
                      <button
                        key={model.id}
                        type="button"
                        className={`platform-button model-button ${platform === model.id ? "active" : ""}`}
                        onClick={() => handlePlatformChange(model.id)}
                      >
                        {model.icon ? (
                          <img
                            src={model.icon}
                            alt={model.name}
                            className="model-icon"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        ) : (
                          <div className="model-icon-placeholder">
                            {model.name.charAt(0)}
                          </div>
                        )}
                        <span title={model.name} className="model-name">
                          {model.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Country Row (Expanded) */}
          {/* {!isCollapsed && (
            <div className="filter-row">
              <span className="row-label">{t("search_analysis.label_country")}</span>
              <div className="time-range-buttons">
                {COUNTRY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`time-button ${selectedCountry === option.value ? "active" : ""}`}
                    onClick={() => setSelectedCountry(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )} */}

          {/* Bottom Right Tools */}
          <div className="bottom-tools">
            <button className="tool-button reset-button" onClick={handleReset}>
              <ReloadOutlined /> {t("search_analysis.action_reset")}
            </button>
            <button
              className="tool-button collapse-button"
              onClick={() => {
                const newCollapsed = !isCollapsed;
                setIsCollapsed(newCollapsed);
                onCollapseChange?.(newCollapsed);
              }}
            >
              {isCollapsed ? <DownOutlined /> : <UpOutlined />}{" "}
              {isCollapsed
                ? t("search_analysis.action_expand")
                : t("search_analysis.action_collapse")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseAnalysisSection;
